import React from "react";

import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { isAdditionalInformationUpdated } from "@src/features/role_request_details/utils/role_request_details_utils";
import { useAgGrid } from "@src/hooks/use_ag_grid";
import { mapRoleRequestData } from "@src/features/resource_plan_v2/utils/resource_plan_table_utils";
import { setAdditionalInformation } from "@src/services/resourcePlanModalSlice";
import { useGetRoleRequestByIdQuery } from "@src/services/slices/projectRoleRequestsApi";
import { useUpdateRoleRequestMutation } from "@src/services/slices/projectsApi";
import { RootState, useAppDispatch, useAppSelector } from "@src/setupStore";
import { AdditionalInformation } from "@src/types";
import {
  ResourcePlanTableRowData,
  RoleRequest,
} from "@src/types/role_request_types";
import { App, Modal } from "antd";

import AdditionalInformationForm from "../additional_information_form/additional_information_form";

interface EditAdditionalInformationModalProps {
  showModal: boolean;
  onClose: () => void;
}

const EditAdditionalInformationModal: React.FC<
  EditAdditionalInformationModalProps
> = ({ showModal, onClose }) => {
  const { gridApi } = useAgGrid();
  const { message } = App.useApp();
  const dispatch = useAppDispatch();

  const [updateRoleRequestMutation] = useUpdateRoleRequestMutation();

  const { selectedResourcePlanRequestId, additionalInformation } =
    useAppSelector((state: RootState) => state.resourcePlanDetailsModalSlice);

  const { projectId } = useAppSelector(
    (state: RootState) => state.resourcePlanSliceV2
  );

  const { data: request } = useGetRoleRequestByIdQuery(
    selectedResourcePlanRequestId,
    {
      skip: !selectedResourcePlanRequestId,
    }
  );

  /**
   * The onSave method to save the update additional information
   */
  async function onSave(): Promise<void> {
    try {
      const roleRequest: RoleRequest = await updateRoleRequestMutation({
        projectId: projectId,
        roleRequestId: request.roleRequestId.toString(),
        requestPayload: { ...request, ...additionalInformation },
      }).unwrap();

      const mappedRoleRequest: ResourcePlanTableRowData[] = mapRoleRequestData([
        roleRequest,
      ]);
      gridApi.applyTransaction({ update: mappedRoleRequest });
      onClose();
      message.success("Changes have been saved successfully");
    } catch (exception) {
      console.error(exception);
    }
  }

  function handleClose() {
    // Reset the state of the properties
    dispatch(
      setAdditionalInformation({
        projectIdentifier: request?.projectIdentifier,
        specificRole: request?.specificRole,
        scope: request?.scope,
      } as AdditionalInformation)
    );
    onClose();
  }

  return (
    <Modal
      open={showModal}
      centered
      maskClosable={false}
      closable={false}
      title="Edit Additional information"
      okButtonProps={{
        icon: <CheckOutlined />,
        disabled: !isAdditionalInformationUpdated(
          additionalInformation,
          request
        ),
        size: "large",
        "data-testid": "save-additional-confirmation",
      }}
      onOk={onSave}
      okText="Save"
      cancelButtonProps={{
        icon: <CloseOutlined />,
        size: "large",
      }}
      onCancel={handleClose}
    >
      <AdditionalInformationForm
        projectIdentifier={request?.projectIdentifier}
        scope={request?.scope}
        specificRole={request?.specificRole}
      />
    </Modal>
  );
};

export default EditAdditionalInformationModal;
